import { menuLabelToKey } from '@/i18n/menuLabel'

/**
 * Look of the sidebar entries: one colour per section, inherited by the plain
 * entries below it — a nested sub-section gets a colour of its own — plus a
 * default icon for entries the backend left without one.
 *
 * Both are keyed by the label slug (`menuLabelToKey`), the same stable key the
 * translations use. The backend stays the owner of the icon: when `menus.icon`
 * is set it always wins, these defaults only fill the gaps. The colour, on the
 * other hand, is purely a client concern — the API has no field for it.
 *
 * Colours are full Tailwind class names written out literally on purpose:
 * Tailwind only generates the classes it can find in the source, so building
 * them with string concatenation would produce colourless icons.
 */

/** Colour of each known section, top-level or nested. */
const SECTION_COLORS: Record<string, string> = {
  panoramica: 'text-sky-400',
  gruppi: 'text-violet-400',
  tuoi_villaggi: 'text-green-400',
  leadership: 'text-amber-300',
  attack_planning: 'text-red-400',
  artefatti: 'text-fuchsia-400',
  defense_planning: 'text-blue-400',
  scout_planning: 'text-teal-400',
  nemici: 'text-rose-400',
  push: 'text-lime-400',
  tool_utili: 'text-slate-300',
  news: 'text-orange-400',
  // Used when the entry sits at the top level; under a section it inherits that colour.
  reservation: 'text-emerald-400',
  prenotazioni: 'text-emerald-400',
  farm_list: 'text-yellow-400',
  lista_farm: 'text-yellow-400',
}

/**
 * Picked for a section that is not in the table above, so an entry added from
 * the admin panel still gets a colour — always the same one for the same label.
 */
const FALLBACK_COLORS = [
  'text-cyan-400',
  'text-emerald-400',
  'text-indigo-400',
  'text-pink-400',
  'text-yellow-300',
  'text-purple-400',
]

/** Icon used when the backend sends `icon: null`. */
const DEFAULT_ICONS: Record<string, string> = {
  // Top-level sections
  panoramica: 'mdi-compass',
  gruppi: 'mdi-account-group',
  tuoi_villaggi: 'mdi-castle',
  leadership: 'mdi-crown',
  attack_planning: 'mdi-sword-cross',
  artefatti: 'mdi-diamond-stone',
  defense_planning: 'mdi-shield-half-full',
  scout_planning: 'mdi-binoculars',
  nemici: 'mdi-skull-crossbones',
  push: 'mdi-barley',
  tool_utili: 'mdi-toolbox',
  news: 'mdi-newspaper-variant',

  // Panoramica
  classifica_giocatori: 'mdi-table-account',
  classifica_alleanze: 'mdi-table-star',
  mappa: 'mdi-map',
  servers: 'mdi-earth',

  // Gruppi
  tuoi_gruppi: 'mdi-account-multiple',
  ranking_gruppi: 'mdi-star-box',
  group_ranking: 'mdi-star-box',
  archive: 'mdi-archive',
  group_archive: 'mdi-archive',

  // I tuoi villaggi
  overview: 'mdi-format-list-numbered',
  archive_servers: 'mdi-archive-clock',

  // Entries shared by several plans
  nuovo_piano: 'mdi-plus-circle',
  piani_salvati: 'mdi-content-save',
  il_tuo_piano: 'mdi-clipboard-text',

  // Leadership
  settings: 'mdi-cog',
  membri: 'mdi-account',
  candidates: 'mdi-account-clock',
  troops: 'mdi-knife-military',
  // Crop finder del gruppo (/group/crop-finder, solo admin): caselle 15c/9c
  crop_finder: 'mdi-barley',
  bot_discord: 'mdi-robot',
  bot_telegram: 'mdi-send',

  // Artefatti
  aggiorna_possessori_artefatti: 'mdi-account-sync',
  rotazione: 'mdi-rotate-3d-variant',
  aggiorna_dati_cata_points_cdthammer: 'mdi-database-refresh',
  pianifica_artefatti: 'mdi-calendar-star',

  // Difese
  incomings: 'mdi-sword',
  recap_incoming: 'mdi-shield-alert',
  muri: 'mdi-wall',
  def_call_stat: 'mdi-chart-bar',
  def_call_stats: 'mdi-chart-bar',
  calc_def_send: 'mdi-timer-sand',
  counter_attack: 'mdi-sword-cross',
  calcola_invio_difese: 'mdi-calculator',
  calcola_falangiata: 'mdi-calculator-variant',
  attuali: 'mdi-clock-outline',
  richieste_ninjate: 'mdi-ninja',
  permanente: 'mdi-infinity',

  // Prenotazioni (/reservation): caselle da fondare, villaggi da conquistare
  reservation: 'mdi-map-marker-check',
  prenotazioni: 'mdi-map-marker-check',

  // Nemici
  traccia_main_nemici: 'mdi-target-account',
  track_enemies: 'mdi-target-account',
  report_main_nemico: 'mdi-file-document-alert',

  // Push
  crea_richiesta_push: 'mdi-cart-plus',
  richieste_push: 'mdi-cart',
  monitoraggio_push: 'mdi-chart-timeline-variant',

  // Liste farm (/farm-list): statistiche dei raid dalle liste incollate
  farm_list: 'mdi-barn',
  lista_farm: 'mdi-barn',

  // Tool utili
  ricerca_inattivi: 'mdi-sleep',
  crop_capital_order: 'mdi-sprout',
  npc_optimization: 'mdi-scale-balance',
  guide_start: 'mdi-home-plus-outline',
}

/**
 * Colour class of a section, given its backend label.
 *
 * `avoid` is the colour of the enclosing section, for a sub-section (an entry
 * with children of its own, e.g. "Pianificazione attacchi" inside "Gruppi"):
 * it must stand apart from its parent, or the two levels would read as one.
 */
export function sectionColor(label: string, avoid: string | null = null): string {
  const key = menuLabelToKey(label)
  const known = SECTION_COLORS[key]
  if (known && known !== avoid) return known

  const pool = FALLBACK_COLORS.filter((color) => color !== avoid)
  let hash = 0
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return pool[hash % pool.length]!
}

/** Default icon for an entry without one, or `null` when we have none either. */
export function defaultMenuIcon(label: string): string | null {
  return DEFAULT_ICONS[menuLabelToKey(label)] ?? null
}
